import { useEffect, useState } from 'react';
import { Copy, Check, ExternalLink, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { formatBRL } from '../lib/currency';
import { useToast } from '../components/ui/ToastSystem';

interface AffiliateData {
  id: string;
  affiliate_code: string;
  status: 'pending' | 'active' | 'suspended';
  commission_type: 'fixed' | 'percentage';
  commission_fixed_brl: number | null;
  commission_pct: number | null;
  pix_key: string | null;
  pix_key_type: string | null;
  tax_id: string | null;
  tax_type: string | null;
  legal_name: string | null;
}

interface CommissionSummary {
  pending:  number;
  approved: number;
  paid:     number;
}

interface ClickStats {
  total: number;
  last30: number;
}

const QR_BASE = 'https://api.qrserver.com/v1/create-qr-code/?size=160x160&bgcolor=1A1D35&color=F5C842&qzone=2&data=';

export function AffiliatePage() {
  const { profile } = useAuth();
  const { showToast } = useToast();

  const [affiliate,   setAffiliate]   = useState<AffiliateData | null>(null);
  const [commissions, setCommissions] = useState<CommissionSummary>({ pending: 0, approved: 0, paid: 0 });
  const [clicks,      setClicks]      = useState<ClickStats>({ total: 0, last30: 0 });
  const [loading,     setLoading]     = useState(true);
  const [copied,      setCopied]      = useState(false);

  // UTM builder
  const [utmSource,   setUtmSource]   = useState('');
  const [utmMedium,   setUtmMedium]   = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmCopied,   setUtmCopied]   = useState(false);

  // Payment info edit
  const [editingPayment, setEditingPayment] = useState(false);
  const [pixKey,      setPixKey]      = useState('');
  const [pixKeyType,  setPixKeyType]  = useState('');
  const [taxId,       setTaxId]       = useState('');
  const [taxType,     setTaxType]     = useState('');
  const [legalName,   setLegalName]   = useState('');
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    if (profile?.id) load();
  }, [profile?.id]);

  async function load() {
    setLoading(true);

    const { data: aff } = await supabase
      .from('affiliates')
      .select('*')
      .eq('user_id', profile!.id)
      .maybeSingle();

    if (!aff) { setLoading(false); return; }

    setAffiliate(aff as AffiliateData);
    setPixKey(aff.pix_key ?? '');
    setPixKeyType(aff.pix_key_type ?? '');
    setTaxId(aff.tax_id ?? '');
    setTaxType(aff.tax_type ?? '');
    setLegalName(aff.legal_name ?? '');

    // Commission summary
    const { data: comms } = await supabase
      .from('affiliate_commissions')
      .select('amount_brl, status')
      .eq('affiliate_id', aff.id);

    const summary = { pending: 0, approved: 0, paid: 0 };
    for (const c of comms ?? []) {
      if (c.status === 'pending')  summary.pending  += Number(c.amount_brl);
      if (c.status === 'approved') summary.approved += Number(c.amount_brl);
      if (c.status === 'paid')     summary.paid     += Number(c.amount_brl);
    }
    setCommissions(summary);

    // Click stats
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const [{ count: total }, { count: last30 }] = await Promise.all([
      supabase
        .from('affiliate_referral_clicks')
        .select('id', { count: 'exact', head: true })
        .eq('affiliate_code', aff.affiliate_code),
      supabase
        .from('affiliate_referral_clicks')
        .select('id', { count: 'exact', head: true })
        .eq('affiliate_code', aff.affiliate_code)
        .gte('created_at', thirtyDaysAgo),
    ]);
    setClicks({ total: total ?? 0, last30: last30 ?? 0 });

    setLoading(false);
  }

  async function savePaymentInfo() {
    setSavingPayment(true);
    const { data, error } = await supabase.functions.invoke('update-affiliate-self', {
      body: {
        pixKey:     pixKey     || undefined,
        pixKeyType: pixKeyType || undefined,
        taxId:      taxId      || undefined,
        taxType:    taxType    || undefined,
        legalName:  legalName  || undefined,
      },
    });
    setSavingPayment(false);
    if (error || data?.error) {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao salvar' });
    } else {
      showToast({ type: 'success', message: 'Dados de pagamento atualizados ✦' });
      setEditingPayment(false);
      load();
    }
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="card shimmer" style={{ height: '200px' }} />
      </div>
    );
  }

  if (!affiliate) {
    return (
      <div className="page-container">
        <h1 className="font-display" style={{ fontSize: 'clamp(1.25rem,3vw,1.5rem)', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
          Programa de Afiliados
        </h1>
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
          <p style={{ fontSize: '2.5rem', margin: '0 0 1rem' }}>✦</p>
          <p style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 'var(--text-label)', margin: '0 0 0.5rem' }}>
            Você ainda não está no programa de afiliados
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0 0 1.5rem', lineHeight: 1.6 }}>
            Entre em contato com o suporte ou aguarde o convite da equipe Professya para se tornar um parceiro afiliado.
          </p>
          <a href="mailto:contato@professya.com" className="btn btn-primary btn-sm">
            Solicitar acesso
          </a>
        </div>
      </div>
    );
  }

  const baseLink = `https://professya.com/ref/${affiliate.affiliate_code}`;

  function buildUtmLink() {
    const params = new URLSearchParams();
    params.set('ref', affiliate!.affiliate_code);
    if (utmSource.trim())   params.set('utm_source',   utmSource.trim());
    if (utmMedium.trim())   params.set('utm_medium',   utmMedium.trim());
    if (utmCampaign.trim()) params.set('utm_campaign', utmCampaign.trim());
    return `https://professya.com?${params.toString()}`;
  }

  const utmLink   = buildUtmLink();
  const qrUrl     = `${QR_BASE}${encodeURIComponent(baseLink)}`;

  const STATUS_LABELS: Record<string, string> = {
    pending:   'Pendente (aguarda aprovação)',
    active:    'Ativo ✦',
    suspended: 'Suspenso',
  };
  const STATUS_COLORS: Record<string, string> = {
    pending: '#fbbf24', active: '#4ade80', suspended: '#f87171',
  };

  const commissionLabel = affiliate.commission_type === 'fixed'
    ? `R$ ${affiliate.commission_fixed_brl ?? 10} por indicação paga`
    : `${((affiliate.commission_pct ?? 0.20) * 100).toFixed(0)}% do valor da venda`;

  async function copyLink(link: string, setCp: (v: boolean) => void) {
    await navigator.clipboard.writeText(link);
    setCp(true);
    setTimeout(() => setCp(false), 2000);
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.25rem,3vw,1.5rem)', color: 'var(--text-primary)', marginBottom: '0.2rem' }}>
          Painel de Afiliado
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '999px',
            backgroundColor: `${STATUS_COLORS[affiliate.status]}22`,
            color: STATUS_COLORS[affiliate.status], fontWeight: 600 }}>
            {STATUS_LABELS[affiliate.status]}
          </span>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
            Comissão: {commissionLabel}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {[
          { label: 'Cliques (30 dias)', value: clicks.last30,              color: 'var(--petrol)' },
          { label: 'Cliques (total)',   value: clicks.total,               color: 'var(--text-primary)' },
          { label: 'Comissões pendentes', value: formatBRL(commissions.pending),  color: '#fbbf24' },
          { label: 'Comissões aprovadas', value: formatBRL(commissions.approved), color: '#60a5fa' },
          { label: 'Total recebido',      value: formatBRL(commissions.paid),     color: '#4ade80' },
        ].map(s => (
          <div key={s.label} className="card" style={{ flex: '1 1 120px', padding: '0.875rem' }}>
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0 0 0.2rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</p>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 700, color: s.color, margin: 0 }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Referral link + QR */}
      <div className="card" style={{ marginBottom: '1.25rem', border: '1px solid var(--border-gold)' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.875rem' }}>
          ✦ Seu link de afiliado
        </p>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ padding: '8px', backgroundColor: '#1A1D35', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-gold)', flexShrink: 0 }}>
            <img src={qrUrl} alt="QR Code" width={80} height={80} style={{ display: 'block', borderRadius: '4px' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <code style={{ flex: 1, padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg-base)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', border: '1px solid var(--border-subtle)' }}>
                {baseLink}
              </code>
              <button className="btn btn-secondary btn-sm" onClick={() => copyLink(baseLink, setCopied)} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                {copied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
              </button>
            </div>
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
              Código: <span style={{ fontFamily: 'monospace', color: 'var(--petrol)' }}>{affiliate.affiliate_code}</span>
              {' · '}
              <a href={baseLink} target="_blank" rel="noopener noreferrer"
                style={{ color: 'var(--petrol)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                Visualizar <ExternalLink size={10} />
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* UTM link builder */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.875rem' }}>
          ✦ Gerador de links com UTM
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.6rem', marginBottom: '0.75rem' }}>
          {[
            { label: 'Fonte (utm_source)', value: utmSource, set: setUtmSource, placeholder: 'instagram, tiktok, email…' },
            { label: 'Meio (utm_medium)',  value: utmMedium, set: setUtmMedium, placeholder: 'stories, bio, feed…' },
            { label: 'Campanha (utm_campaign)', value: utmCampaign, set: setUtmCampaign, placeholder: 'lancamento_2026…' },
          ].map(f => (
            <label key={f.label} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{f.label}</span>
              <input className="form-input" style={{ fontSize: 'var(--text-caption)' }}
                value={f.value} onChange={e => f.set(e.target.value)} placeholder={f.placeholder} />
            </label>
          ))}
        </div>

        {/* Templates rápidos */}
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', alignSelf: 'center' }}>Templates:</span>
          {[
            { label: 'Instagram Stories', s: 'instagram',  m: 'stories',     c: '' },
            { label: 'Instagram Bio',     s: 'instagram',  m: 'bio',         c: '' },
            { label: 'TikTok Bio',        s: 'tiktok',     m: 'bio',         c: '' },
            { label: 'YouTube',           s: 'youtube',    m: 'description', c: '' },
            { label: 'WhatsApp',          s: 'whatsapp',   m: 'status',      c: '' },
          ].map(t => (
            <button key={t.label} className="btn btn-ghost btn-sm" style={{ fontSize: '11px' }}
              onClick={() => { setUtmSource(t.s); setUtmMedium(t.m); setUtmCampaign(t.c); }}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <code style={{ flex: 1, padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg-base)', borderRadius: 'var(--radius-sm)', fontSize: '11px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', border: '1px solid var(--border-subtle)' }}>
            {utmLink}
          </code>
          <button className="btn btn-primary btn-sm" onClick={() => copyLink(utmLink, setUtmCopied)} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            {utmCopied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
          </button>
        </div>
      </div>

      {/* Share shortcuts */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.875rem' }}>
          ✦ Compartilhar
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {[
            {
              label: '💬 WhatsApp',
              onClick: () => window.open(
                `https://api.whatsapp.com/send?text=${encodeURIComponent(`Descubra o Professya, astrologia que transforma! ✦\n${baseLink}`)}`,
                '_blank',
              ),
            },
            {
              label: '𝕏 Twitter / X',
              onClick: () => window.open(
                `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Descobri o Professya, astrologia personalizada incrível! Cria tua conta grátis: ${baseLink}`)}`,
                '_blank',
              ),
            },
            {
              label: '✉️ E-mail',
              onClick: () => {
                window.location.href = `mailto:?subject=${encodeURIComponent('Conheça o Professya')}&body=${encodeURIComponent(`Oi!\n\nVou te indicar o Professya, uma plataforma de astrologia personalizada incrível.\n\nCria tua conta grátis aqui: ${baseLink}\n\nAté mais!`)}`;
              },
            },
          ].map(b => (
            <button key={b.label} className="btn btn-secondary" onClick={b.onClick}
              style={{ flex: '1 1 120px' }}>
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {/* Payment info */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
            ✦ Dados de pagamento
          </p>
          {!editingPayment && (
            <button className="btn btn-ghost btn-sm" onClick={() => setEditingPayment(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <RefreshCw size={12} /> Editar
            </button>
          )}
        </div>

        {!editingPayment ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {[
              { label: 'Nome legal / razão social', value: affiliate.legal_name },
              { label: `${affiliate.tax_type?.toUpperCase() ?? 'CPF/CNPJ'}`, value: affiliate.tax_id },
              { label: `Chave PIX (${affiliate.pix_key_type ?? '-'})`, value: affiliate.pix_key },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', gap: '0.75rem', fontSize: 'var(--text-caption)' }}>
                <span style={{ color: 'var(--text-muted)', minWidth: '160px' }}>{r.label}:</span>
                <span style={{ color: r.value ? 'var(--text-primary)' : 'var(--text-muted)', fontStyle: r.value ? 'normal' : 'italic', fontFamily: r.label.includes('PIX') || r.label.includes('CPF') || r.label.includes('CNPJ') ? 'monospace' : 'inherit' }}>
                  {r.value ?? 'não informado'}
                </span>
              </div>
            ))}
            {!affiliate.pix_key && (
              <p style={{ fontSize: '11px', color: '#fbbf24', margin: '0.25rem 0 0', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(251,191,36,0.08)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(251,191,36,0.2)' }}>
                ⚠ Adicione sua chave PIX para receber pagamentos de comissão.
              </p>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <span className="form-label">Nome legal / razão social</span>
              <input className="form-input" value={legalName} onChange={e => setLegalName(e.target.value)} placeholder="Como consta no documento" />
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <span className="form-label">Tipo de documento</span>
                <select className="form-select" value={taxType} onChange={e => setTaxType(e.target.value)}>
                  <option value="">Não informado</option>
                  <option value="cpf">CPF (Pessoa Física)</option>
                  <option value="cnpj">CNPJ (Pessoa Jurídica)</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <span className="form-label">CPF / CNPJ</span>
                <input className="form-input" value={taxId} onChange={e => setTaxId(e.target.value)} placeholder="000.000.000-00" />
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <span className="form-label">Tipo de chave PIX</span>
                <select className="form-select" value={pixKeyType} onChange={e => setPixKeyType(e.target.value)}>
                  <option value="">Não informado</option>
                  <option value="cpf">CPF</option>
                  <option value="cnpj">CNPJ</option>
                  <option value="email">E-mail</option>
                  <option value="phone">Telefone</option>
                  <option value="random">Chave aleatória</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                <span className="form-label">Chave PIX</span>
                <input className="form-input" value={pixKey} onChange={e => setPixKey(e.target.value)} />
              </label>
            </div>

            <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
              Seus dados fiscais são necessários para emissão de recibo de pagamento (RPA para CPF ou nota fiscal para CNPJ) conforme legislação brasileira.
            </p>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={savePaymentInfo} disabled={savingPayment}>
                {savingPayment ? 'Salvando...' : 'Salvar dados'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditingPayment(false)}>Cancelar</button>
            </div>
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="card">
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1rem' }}>
          ✦ Como funciona
        </p>
        <ol style={{ margin: 0, padding: '0 0 0 1.25rem' }}>
          {[
            `Compartilhe seu link único (${baseLink})`,
            'Quando alguém cria uma conta usando seu link, a indicação é registrada',
            `Quando essa pessoa assinar qualquer plano pago, você ganha ${commissionLabel}`,
            'Comissões ficam "pendentes" por alguns dias e são aprovadas pela equipe',
            'Pagamentos são processados mensalmente via PIX na chave cadastrada acima',
          ].map((s, i) => (
            <li key={i} style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.6rem', lineHeight: 1.5 }}>
              {s}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
